import { useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import { Play, Download, Eye, Clock, HardDrive, Database, FolderArchive } from 'lucide-react'
import Heading from '@/components/heading'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import AppLayout from '@/layouts/app-layout'
import { serversPath, appPath, appBackupsPath, appBackupPath, downloadAppBackupPath } from '@/routes'
import type { BreadcrumbItem } from '@/types'
import type { App, Backup, BackupConfig } from '@/types'

type BackupsIndexProps = {
  app: Pick<App, 'id' | 'name'>
  backups: (Backup & { backup_config: Pick<BackupConfig, 'id' | 'backup_type' | 'destination_type'> })[]
  backup_configs: Pick<BackupConfig, 'id' | 'backup_type' | 'enabled'>[]
}

type FrequencyFilter = 'all' | 'daily' | 'weekly' | 'monthly' | 'manual'

export default function BackupsIndex({ app, backups, backup_configs }: BackupsIndexProps) {
  const [filter, setFilter] = useState<FrequencyFilter>('all')

  const breadcrumbs: BreadcrumbItem[] = [
    { title: 'Servers', href: serversPath() },
    { title: app.name, href: appPath(app.id) },
    { title: 'Backups', href: appBackupsPath(app.id) },
  ]

  const filteredBackups = filter === 'all' 
    ? backups 
    : backups.filter(b => b.frequency_type === filter)

  function triggerManualBackup(configId: number) {
    router.post(appBackupsPath(app.id), { backup_config_id: configId })
  }

  function formatBytes(bytes: number | null | undefined): string {
    if (!bytes) return 'N/A'
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    if (bytes === 0) return '0 Bytes'
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i]
  }

  function formatDate(date: string | null | undefined): string {
    if (!date) return 'N/A'
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  function getStatusVariant(status: string) {
    switch (status) {
      case 'success': return 'default'
      case 'error': return 'destructive'
      case 'running': return 'outline'
      default: return 'secondary'
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Backups - ${app.name}`} />

      <div className="p-6">
        <div className="flex items-center justify-between">
        <Heading
          title={`Backups for ${app.name}`}
          description="View and manage backup history"
        />
      </div>

      {/* Manual Backup Section */}
      {backup_configs.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-base">Run Manual Backup</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {backup_configs.filter(c => c.enabled).map((config) => (
              <Button
                key={config.id}
                variant="outline"
                size="sm"
                onClick={() => triggerManualBackup(config.id)}
              >
                <Play className="mr-2 h-4 w-4" />
                {config.backup_type === 'database' ? (
                  <Database className="mr-1 h-4 w-4" />
                ) : (
                  <FolderArchive className="mr-1 h-4 w-4" />
                )}
                {config.backup_type}
              </Button>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Filter Tabs */}
      <div className="mt-6 flex gap-2 border-b pb-4">
        {(['all', 'daily', 'weekly', 'monthly', 'manual'] as FrequencyFilter[]).map((freq) => (
          <button
            key={freq}
            onClick={() => setFilter(freq)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              filter === freq
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted'
            }`}
          >
            {freq.charAt(0).toUpperCase() + freq.slice(1)}
          </button>
        ))}
      </div>

      {/* Backups List */}
      <div className="mt-6">
        {filteredBackups.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">No backups found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="pb-3 text-left font-medium">Type</th>
                  <th className="pb-3 text-left font-medium">Frequency</th>
                  <th className="pb-3 text-left font-medium">Status</th>
                  <th className="pb-3 text-left font-medium">Size</th>
                  <th className="pb-3 text-left font-medium">Completed</th>
                  <th className="pb-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredBackups.map((backup) => (
                  <tr key={backup.id} className="border-b last:border-0">
                    <td className="py-3">
                      <span className="flex items-center gap-2">
                        {backup.backup_config.backup_type === 'database' ? (
                          <Database className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <FolderArchive className="h-4 w-4 text-muted-foreground" />
                        )}
                        {backup.backup_config.backup_type}
                      </span>
                    </td>
                    <td className="py-3 capitalize">{backup.frequency_type}</td>
                    <td className="py-3">
                      <Badge variant={getStatusVariant(backup.status)}>
                        {backup.status}
                      </Badge>
                    </td>
                    <td className="py-3">
                      <span className="flex items-center gap-1">
                        <HardDrive className="h-3 w-3" />
                        {formatBytes(backup.file_size)}
                      </span>
                    </td>
                    <td className="py-3">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(backup.completed_at)}
                      </span>
                    </td>
                    <td className="py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="icon" asChild>
                          <Link href={appBackupPath(app.id, backup.id)}>
                            <Eye className="h-4 w-4" />
                          </Link>
                        </Button>
                        {backup.status === 'success' && backup.file_path && (
                          <Button variant="ghost" size="icon" asChild>
                            <a href={downloadAppBackupPath(app.id, backup.id)}>
                              <Download className="h-4 w-4" />
                            </a>
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </div>
    </AppLayout>
  )
}
